/**
 * Address parsing utility for US real estate contracts
 * Handles various address formats and extracts components
 */

/**
 * Parse a full address string into street, city, state, and zip components
 * Handles multiple formats:
 * - "123 Main St, Boston, MA 02101"
 * - "123 Main St Boston MA 02101"
 * - "123 Main St, Boston MA, 02101"
 * @param {string} addressString - The full address to parse
 * @returns {Object} - Object with street, city, state, zip properties
 */
export const parseAddress = (addressString) => {
  if (!addressString || typeof addressString !== 'string') {
    return {
      street: null,
      city: null,
      state: null,
      zip: null,
      fullAddress: addressString
    };
  }

  const address = addressString.trim();

  // Try multiple parsing strategies

  // Strategy 1: Split by comma (most common in US addresses)
  if (address.includes(',')) {
    return parseAddressByComma(address);
  }

  // Strategy 2: Split by state abbreviation + zip pattern
  const stateZipRegex = /([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i;
  const stateZipMatch = address.match(stateZipRegex);

  if (stateZipMatch) {
    return parseAddressByStateZip(address, stateZipMatch);
  }

  // Strategy 3: Fallback - treat entire string as street address
  return {
    street: address,
    city: null,
    state: null,
    zip: null,
    fullAddress: address
  };
};

/**
 * Parse address formatted with commas
 * Example: "123 Main St, Boston, MA 02101"
 */
const parseAddressByComma = (address) => {
  const parts = address.split(',').map(p => p.trim());

  let street = parts[0] || null;
  let city = parts[1] || null;
  let state = null;
  let zip = null;

  // If we have a third part, try to extract state and zip
  if (parts[2]) {
    const thirdPart = parts[2].trim();
    const stateZipMatch = thirdPart.match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/i);

    if (stateZipMatch) {
      state = stateZipMatch[1].toUpperCase();
      zip = stateZipMatch[2];
    } else {
      // If no state/zip pattern, treat as part of city
      if (!city) {
        city = thirdPart;
      }
    }
  }

  return {
    street,
    city,
    state,
    zip,
    fullAddress: address
  };
};

/**
 * Parse address by identifying state and zip code at the end
 * Example: "123 Main St Boston MA 02101"
 */
const parseAddressByStateZip = (address, stateZipMatch) => {
  const state = stateZipMatch[1].toUpperCase();
  const zip = stateZipMatch[2];

  // Get the part before state and zip
  const beforeStateZip = address
    .substring(0, stateZipMatch.index)
    .trim();

  // Try to split the remaining into street and city
  const parts = beforeStateZip.split(/\s+(?=[A-Z])/);

  let street = null;
  let city = null;

  if (parts.length >= 2) {
    // Last part is likely city (title case word before state)
    city = parts[parts.length - 1];
    street = parts.slice(0, -1).join(' ');
  } else if (parts.length === 1) {
    street = parts[0];
  }

  return {
    street,
    city,
    state,
    zip,
    fullAddress: address
  };
};

/**
 * Extract just the street address from a full address string
 * This handles the case where property_street in database contains full address
 * @param {string} fullAddress - The full address (may be in property_street column)
 * @returns {string} - Just the street address portion
 */
export const getStreetAddressOnly = (fullAddress) => {
  if (!fullAddress) return null;

  // If already just a street (no commas and no state/zip pattern), return as-is
  if (!fullAddress.includes(',') && !fullAddress.match(/[A-Z]{2}\s+\d{5}/)) {
    return fullAddress.trim();
  }

  // Parse the address and return just the street
  const parsed = parseAddress(fullAddress);
  return parsed.street || fullAddress;
};

/**
 * Format address components back into a full address string
 * @param {Object} components - Object with street, city, state, zip
 * @returns {string} - Formatted full address
 */
export const formatAddress = (components) => {
  const { street, city, state, zip } = components;
  const parts = [];

  if (street) parts.push(street);
  if (city) parts.push(city);

  const stateZip = [];
  if (state) stateZip.push(state);
  if (zip) stateZip.push(zip);

  if (stateZip.length > 0) {
    parts.push(stateZip.join(' '));
  }

  return parts.join(', ');
};

/**
 * Get a display-friendly version of an address
 * @param {string} fullAddress - The full address
 * @returns {string} - Display version (street, city, state)
 */
export const getDisplayAddress = (fullAddress) => {
  const parsed = parseAddress(fullAddress);
  const parts = [];

  if (parsed.street) parts.push(parsed.street);
  if (parsed.city) parts.push(parsed.city);
  if (parsed.state) parts.push(parsed.state);

  return parts.join(', ');
};

export default {
  parseAddress,
  getStreetAddressOnly,
  formatAddress,
  getDisplayAddress
};
